/**
 * Metabolics Custom Cart Drawer
 * Replaces theme default — handles upsells, free gifts, progress bar
 * Features: smart liquid→dropper upsell, Recharge subscription toggle
 */

class MetabolicsCartDrawer {
  constructor(drawer) {
    this.drawer = drawer;
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.config = JSON.parse(drawer.dataset.cartConfig || '{}');
    this.productCache = {};
    this.multibuyCache = {};
    this.isManagingGift = false;
    this.isOpen = false;

    this.els = {
      items: drawer.querySelector('[data-cart-items]'),
      empty: drawer.querySelector('[data-cart-empty]'),
      footer: drawer.querySelector('[data-cart-footer]'),
      upsells: drawer.querySelector('[data-cart-upsells]'),
      progressFill: drawer.querySelector('[data-progress-fill]'),
      progressMsg: drawer.querySelector('[data-progress-message]'),
      milestone2: drawer.querySelector('[data-milestone="2"]'),
      subtotalPrice: drawer.querySelector('[data-subtotal-price]'),
      subtotalCompare: drawer.querySelector('[data-subtotal-compare]'),
      itemCount: drawer.querySelector('[data-item-count]'),
    };

    this.bindEvents();
  }

  /* ── Events ─────────────────────────────────── */

  bindEvents() {
    // Close buttons
    this.drawer.querySelectorAll('[data-cart-close]').forEach(btn =>
      btn.addEventListener('click', () => this.close())
    );
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Delegated clicks inside drawer
    this.drawer.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      const key = btn.closest('[data-line-key]')?.dataset.lineKey;
      const vid = btn.dataset.variantId;
      const sellingPlan = btn.dataset.sellingPlan || null;

      switch (action) {
        case 'increase': this.changeQty(key, 1); break;
        case 'decrease': this.changeQty(key, -1); break;
        case 'remove': this.removeItem(key); break;
        case 'add-upsell': this.addUpsellToCart(btn); break;
      }
    });

    // Subscribe toggle on upsell cards
    this.drawer.addEventListener('change', (e) => {
      const toggle = e.target.closest('.cd-subscribe__toggle input');
      if (toggle) this.handleSubscribeToggle(toggle);

      const freqSelect = e.target.closest('.cd-subscribe__freq');
      if (freqSelect) this.handleFrequencyChange(freqSelect);

      // Variant selector on upsell cards
      const variantSelect = e.target.closest('[data-upsell-variant-select]');
      if (variantSelect) {
        const card = variantSelect.closest('[data-upsell-card]');
        if (card) {
          const addBtn = card.querySelector('[data-action="add-upsell"]');
          if (addBtn) addBtn.dataset.variantId = variantSelect.value;
        }
      }
    });

    // Intercept theme's native cart-drawer open triggers
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest(
        '#cart-icon-bubble, ' +
        '.header__icon--cart, ' +
        'a[href="/cart"], ' +
        'button[aria-controls="CartDrawer"], ' +
        '[data-js-sidebar-handle][aria-controls="site-cart-sidebar"], ' +
        '.mobile-cart-button'
      );
      if (cartLink) {
        e.preventDefault();
        e.stopPropagation();
        this.open();
        return;
      }
    }, true);

    // Escape key closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Listen for custom cart-open events
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:refresh', () => this.refresh());

    // Suppress the theme's native cart-drawer
    this.suppressNativeDrawer();
  }

  suppressNativeDrawer() {
    const nativeDrawer = document.querySelector('cart-drawer');
    if (nativeDrawer && nativeDrawer !== this.drawer) {
      nativeDrawer.open = () => { this.open(); };
      nativeDrawer.renderContents = () => { this.open(); };
      nativeDrawer.style.display = 'none';
    }
  }

  /* ── Open / Close ───────────────────────────── */

  open() {
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.drawer.removeAttribute('aria-hidden');
    this.drawer.style.display = '';
    if (this.overlay) this.overlay.classList.add('is-visible');
    document.body.classList.add('cart-drawer-open');
    document.body.style.overflow = 'hidden';
    this.refresh();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    if (this.overlay) this.overlay.classList.remove('is-visible');
    document.body.classList.remove('cart-drawer-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!this.isOpen) this.drawer.style.display = 'none';
    }, 350);
  }

  /* ── AJAX Cart Operations ───────────────────── */

  async fetchCart() {
    const res = await fetch('/cart.json');
    return res.json();
  }

  async addToCart(variantId, qty, properties, sellingPlan) {
    const body = { id: parseInt(variantId), quantity: qty };
    if (properties) body.properties = properties;
    if (sellingPlan) body.selling_plan = parseInt(sellingPlan);
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    this.updateHeaderCount();
    await this.refresh();
  }

  async addUpsellToCart(btn) {
    const card = btn.closest('.cd-upsell');
    // Read variant from dropdown if it exists, otherwise from button data
    let vid = btn.dataset.variantId;
    if (card) {
      const variantSelect = card.querySelector('[data-upsell-variant-select]');
      if (variantSelect) vid = variantSelect.value;
    }
    let sellingPlan = null;

    if (card) {
      const subRadio = card.querySelector('.cd-subscribe__toggle input[value="subscribe"]:checked');
      if (subRadio) {
        const freqSelect = card.querySelector('.cd-subscribe__freq');
        if (freqSelect && freqSelect.value) {
          sellingPlan = freqSelect.value;
        }
      }
    }

    await this.addToCart(vid, 1, null, sellingPlan);
  }

  async updateItem(key, qty) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty }),
    });
    this.updateHeaderCount();
    await this.refresh();
  }

  async changeQty(key, delta) {
    const cart = await this.fetchCart();
    const item = cart.items.find(i => i.key === key);
    if (!item) return;
    const newQty = Math.max(0, item.quantity + delta);
    await this.updateItem(key, newQty);
  }

  async removeItem(key) {
    await this.updateItem(key, 0);
  }

  /* ── Refresh / Render ───────────────────────── */

  async refresh() {
    const cart = await this.fetchCart();
    await this.manageFreeGift(cart);
    const freshCart = this.isManagingGift ? await this.fetchCart() : cart;
    this.renderAll(freshCart);
  }

  renderAll(cart) {
    this.renderProgressBar(cart);
    this.renderCartItems(cart);
    this.renderFooter(cart);
    this.renderUpsells(cart);
    this.updateHeaderCount(cart);
  }

  /* ── Header Cart Count ──────────────────────── */

  updateHeaderCount(cart) {
    if (cart) {
      document.querySelectorAll(
        '[data-header-cart-count], .cart-count-bubble span[aria-hidden], .cart-count'
      ).forEach(el => {
        el.textContent = cart.item_count;
      });
    }
  }

  /* ── Progress Bar ───────────────────────────── */

  renderProgressBar(cart) {
    const threshold = (this.config.milestone2Threshold || 40) * 100;
    const total = this.getCartTotalExcludingGift(cart);

    const pct = Math.min(100, (total / threshold) * 100);
    if (this.els.progressFill) {
      this.els.progressFill.style.width = pct + '%';
    }

    const hit = total >= threshold;
    if (this.els.milestone2) {
      this.els.milestone2.classList.toggle('is-unlocked', hit);
    }

    if (this.els.progressMsg) {
      if (hit) {
        this.els.progressMsg.innerHTML = `You've unlocked <strong>free shipping!</strong>`;
        this.els.progressMsg.classList.add('is-complete');
      } else if (cart.item_count > 0) {
        const remaining = this.money(Math.max(0, threshold - total));
        this.els.progressMsg.innerHTML = `You're <strong>${remaining}</strong> away from free shipping!`;
        this.els.progressMsg.classList.remove('is-complete');
      } else {
        this.els.progressMsg.innerHTML = '';
        this.els.progressMsg.classList.remove('is-complete');
      }
    }
  }

  getCartTotalExcludingGift(cart) {
    let total = 0;
    cart.items.forEach(item => {
      if (!this.isFreeGiftItem(item)) {
        total += item.final_line_price;
      }
    });
    return total;
  }

  isFreeGiftItem(item) {
    return item.properties && item.properties._free_gift === 'true';
  }

  /* ── Free Gift Management ───────────────────── */

  async manageFreeGift(cart) {
    if (!this.config.enableFreeGift || this.isManagingGift) return;
    this.isManagingGift = true;

    const threshold = (this.config.milestone2Threshold || 40) * 100;
    const total = this.getCartTotalExcludingGift(cart);
    const giftKey = this.findFreeGiftKey(cart);

    try {
      if (total >= threshold && !giftKey) {
        const variantId = this.config.freeGiftVariantId;
        if (variantId) {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: parseInt(variantId),
              quantity: 1,
              properties: { _free_gift: 'true' },
            }),
          });
        }
      } else if (total < threshold && giftKey) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftKey, quantity: 0 }),
        });
      }
    } catch (e) {
      console.error('Free gift error:', e);
    }

    this.isManagingGift = false;
  }

  findFreeGiftKey(cart) {
    const item = cart.items.find(i => this.isFreeGiftItem(i));
    return item ? item.key : null;
  }

  /* ── Cart Items ─────────────────────────────── */

  renderCartItems(cart) {
    if (!this.els.items) return;

    const nonGiftItems = cart.items.filter(i => !this.isFreeGiftItem(i));
    const giftItems = cart.items.filter(i => this.isFreeGiftItem(i));
    const hasItems = cart.items.length > 0;

    if (this.els.empty) {
      this.els.empty.style.display = hasItems ? 'none' : 'flex';
    }
    if (this.els.footer) {
      this.els.footer.style.display = hasItems ? '' : 'none';
    }
    if (this.els.upsells) {
      this.els.upsells.style.display = hasItems ? '' : 'none';
    }

    let html = '';
    giftItems.forEach(item => { html += this.renderGiftItem(item); });
    nonGiftItems.forEach(item => { html += this.renderRegularItem(item); });
    this.els.items.innerHTML = html;

    // Inject multibuy nudge banners (async, after initial render)
    this.injectMultibuyNudges(nonGiftItems);
  }

  /**
   * Fetches multibuy tier data from product metafields.
   * Reads custom.multibuy_tiers from the product .js endpoint.
   * Requires the metafield to have Storefront API access enabled.
   */
  async getMultibuyTiers(handle) {
    if (this.multibuyCache[handle] !== undefined) return this.multibuyCache[handle];
    try {
      const product = await this.getProduct(handle);
      if (!product) {
        this.multibuyCache[handle] = null;
        return null;
      }
      // Look for the multibuy_tiers metafield in the product data
      // Shopify .js endpoint includes metafields if they have storefront access
      const metafields = product.metafields || {};
      const customMf = metafields.custom || {};
      let tiersData = customMf.multibuy_tiers || null;

      // Also check product.metafield (some themes use this)
      if (!tiersData && product.metafield) {
        tiersData = product.metafield.custom?.multibuy_tiers || null;
      }

      if (tiersData) {
        const parsed = typeof tiersData === 'string' ? JSON.parse(tiersData) : tiersData;
        this.multibuyCache[handle] = parsed;
        return parsed;
      }

      this.multibuyCache[handle] = null;
      return null;
    } catch (e) {
      this.multibuyCache[handle] = null;
      return null;
    }
  }

  /**
   * Injects multibuy nudge banners into eligible cart items.
   * Only shows for one-time purchases (no selling_plan_allocation).
   */
  async injectMultibuyNudges(items) {
    for (const item of items) {
      // Skip subscription items — multibuy is one-time only
      if (item.selling_plan_allocation) continue;

      const tiersData = await this.getMultibuyTiers(item.handle);
      if (!tiersData || !tiersData.tiers || tiersData.tiers.length === 0) continue;

      // Only show if purchase type matches (default: onetime)
      if (tiersData.purchase_type && tiersData.purchase_type !== 'onetime') continue;

      const nudgeHtml = this.renderMultibuyNudge(item, tiersData);
      if (!nudgeHtml) continue;

      // Find the cart item element and append the nudge
      const itemEl = this.els.items.querySelector(`[data-line-key="${item.key}"]`);
      if (itemEl && !itemEl.querySelector('.cd-multibuy')) {
        const nudgeWrapper = document.createElement('div');
        nudgeWrapper.innerHTML = nudgeHtml;
        itemEl.appendChild(nudgeWrapper.firstElementChild);
      }
    }
  }

  /**
   * Renders a multibuy nudge banner for a cart item.
   * Shows tier progression and encourages higher quantities.
   */
  renderMultibuyNudge(item, tiersData) {
    const tiers = tiersData.tiers;
    const qty = item.quantity;
    const displayName = tiersData.display_name || 'BUY MORE SAVE MORE';

    // Find the active and next tier
    let activeTier = null;
    let nextTier = null;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].qty) {
        activeTier = tiers[i];
        break;
      }
    }
    // Find the next tier above active
    for (let i = 0; i < tiers.length; i++) {
      if (qty < tiers[i].qty) {
        nextTier = tiers[i];
        break;
      }
    }

    const isHighestTier = activeTier && !nextTier;
    const hasNoTier = !activeTier;

    let message = '';
    let icon = '';

    if (isHighestTier) {
      // At highest tier — congratulations!
      message = `<strong>${activeTier.discount_pct}% off</strong> applied at checkout!`;
      icon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>`;
    } else if (activeTier && nextTier) {
      // At a tier with higher available
      const moreNeeded = nextTier.qty - qty;
      message = `<strong>${activeTier.discount_pct}% off</strong> at checkout! Add ${moreNeeded} more for <strong>${nextTier.discount_pct}% off</strong>`;
      icon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
    } else if (hasNoTier && nextTier) {
      // Below any tier
      const moreNeeded = nextTier.qty - qty;
      message = `Add ${moreNeeded} more for <strong>${nextTier.discount_pct}% off</strong> at checkout!`;
      icon = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
    } else {
      return null;
    }

    const stateClass = isHighestTier ? 'cd-multibuy--complete' : (activeTier ? 'cd-multibuy--partial' : 'cd-multibuy--nudge');

    return `
      <div class="cd-multibuy ${stateClass}">
        <div class="cd-multibuy__inner">
          <span class="cd-multibuy__icon">${icon}</span>
          <span class="cd-multibuy__msg">${message}</span>
        </div>
      </div>`;
  }

  renderGiftItem(item) {
    const img = this.getItemImage(item, 120);
    return `
      <div class="cd-item cd-item--gift" data-line-key="${item.key}">
        <div class="cd-item__img">
          <img src="${img}" alt="${this.esc(item.title)}" loading="lazy" width="80" height="80">
        </div>
        <div class="cd-item__info">
          <div class="cd-item__title">${this.esc(item.product_title)}</div>
          <span class="cd-item__badge cd-item__badge--gift">Free Gift</span>
        </div>
        <div class="cd-item__right">
          <button class="cd-item__remove" data-action="remove" aria-label="Remove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/></svg>
          </button>
          <div class="cd-item__price">
            <span class="cd-item__price-sale">£0.00</span>
            ${item.original_price > 0 ? `<span class="cd-item__price-compare">${this.money(item.original_price)}</span>` : ''}
          </div>
        </div>
      </div>`;
  }

  renderRegularItem(item) {
    const img = this.getItemImage(item, 120);
    const hasDiscount = item.original_line_price > item.final_line_price;
    const discountLabel = this.getDiscountLabel(item);

    return `
      <div class="cd-item" data-line-key="${item.key}">
        <div class="cd-item__img">
          <img src="${img}" alt="${this.esc(item.title)}" loading="lazy" width="80" height="80">
        </div>
        <div class="cd-item__info">
          <div class="cd-item__title">${this.esc(item.product_title)}</div>
          ${discountLabel ? `<span class="cd-item__badge cd-item__badge--discount">${this.esc(discountLabel)}</span>` : ''}
          <div class="cd-item__qty">
            <button class="cd-item__qty-btn" data-action="decrease" aria-label="Decrease">
              <svg width="12" height="12" viewBox="0 0 12 12"><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" stroke-width="1.5"/></svg>
            </button>
            <span class="cd-item__qty-num">${item.quantity}</span>
            <button class="cd-item__qty-btn" data-action="increase" aria-label="Increase">
              <svg width="12" height="12" viewBox="0 0 12 12"><line x1="6" y1="2" x2="6" y2="10" stroke="currentColor" stroke-width="1.5"/><line x1="2" y1="6" x2="10" y2="6" stroke="currentColor" stroke-width="1.5"/></svg>
            </button>
          </div>
        </div>
        <div class="cd-item__right">
          <button class="cd-item__remove" data-action="remove" aria-label="Remove">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14"/></svg>
          </button>
          <div class="cd-item__price">
            <span class="cd-item__price-sale">${this.money(item.final_line_price)}</span>
            ${hasDiscount ? `<span class="cd-item__price-compare">${this.money(item.original_line_price)}</span>` : ''}
          </div>
        </div>
      </div>`;
  }

  getDiscountLabel(item) {
    if (!item.discount_allocations || !item.discount_allocations.length) return '';

    var alloc = item.discount_allocations[0];
    var title = alloc.discount_application?.title || '';

    // Clean up Recharge multibuy names: "BUY MORE SAVE MORE (EZ1UM-0)" → clean label
    if (/buy more|multi.?buy|save more/i.test(title)) {
      // Calculate actual % from the discount amount
      if (alloc.amount && item.original_line_price > 0) {
        var pct = Math.round((alloc.amount / item.original_line_price) * 100);
        if (pct > 0) return 'Multibuy: Save ' + pct + '%';
      }
      return 'Multibuy discount';
    }

    // For other discounts, just strip parenthesized codes
    title = title.replace(/\s*\([A-Z0-9_-]+\)\s*$/i, '').trim();
    return title;
  }

  getItemImage(item, size) {
    if (item.featured_image && item.featured_image.url) {
      return item.featured_image.url.replace(/(\.\w+)(\?|$)/, `_${size}x$1$2`);
    }
    if (item.image) return this.resizeImage(item.image, size);
    return '';
  }

  resizeImage(url, size) {
    if (!url) return '';
    return url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|master|\d+x\d+|x\d+|\d+x)\./, `_${size}x.`);
  }

  /* ── Smart Upsell Engine ────────────────────── */

  /**
   * Detects if a cart item is a liquid product based on title.
   * Metabolics liquid products always have "Liquid" or "Tincture" in title.
   */
  isLiquidProduct(item) {
    const title = (item.product_title || item.title || '').toLowerCase();
    return title.includes('liquid') || title.includes('tincture');
  }

  /**
   * Resolves upsells with a priority cascade:
   * 1. Liquid in cart → dropper bottle
   * 2. Block-based trigger rules
   * 3. Fallback collection products
   */
  resolveUpsells(cart) {
    const cartHandles = cart.items.map(i => i.handle);
    const maxUpsells = this.config.maxUpsells || 3;
    let matched = [];
    let heading = this.config.generalUpsellHeading || 'Products related to this item';

    // ── Priority 1: Liquid → Dropper bottle ──
    const dropperHandle = this.config.dropperBottleHandle || '';
    if (dropperHandle) {
      const hasLiquid = cart.items.some(i => this.isLiquidProduct(i) && !this.isFreeGiftItem(i));
      if (hasLiquid && !cartHandles.includes(dropperHandle)) {
        const dropperHeading = this.config.dropperBottleHeading || 'Need a dropper bottle?';
        matched.push({ handle: dropperHandle, heading: dropperHeading, isDropper: true });
      }
    }

    // ── Priority 2: Block-based rules ──
    const rules = this.config.upsellRules || [];
    for (const rule of rules) {
      if (matched.length >= maxUpsells) break;
      if (cartHandles.includes(rule.trigger) && !cartHandles.includes(rule.upsell)) {
        if (!matched.find(m => m.handle === rule.upsell)) {
          matched.push({ handle: rule.upsell, heading: rule.heading });
        }
      }
    }

    // ── Priority 3: Fallback collection ──
    if (matched.length < maxUpsells) {
      const fallbacks = (this.config.fallbackProducts || [])
        .filter(h => !cartHandles.includes(h) && !matched.find(m => m.handle === h));
      for (const h of fallbacks) {
        if (matched.length >= maxUpsells) break;
        matched.push({ handle: h, heading: null });
      }
    }

    // Determine heading: use first match's custom heading if available
    const firstCustom = matched.find(m => m.heading && !m.isDropper);
    if (firstCustom) heading = firstCustom.heading;

    // If the ONLY upsell is the dropper, use its heading
    if (matched.length > 0 && matched.every(m => m.isDropper)) {
      heading = matched[0].heading;
    }

    return { matched: matched.slice(0, maxUpsells), heading };
  }

  async renderUpsells(cart) {
    if (!this.els.upsells || cart.items.length === 0) return;

    const { matched, heading } = this.resolveUpsells(cart);

    if (matched.length === 0) {
      this.els.upsells.innerHTML = '';
      return;
    }

    // Fetch all products (with selling plan data)
    const products = await Promise.all(
      matched.map(async (m) => {
        const product = await this.getProduct(m.handle);
        if (product) product._isDropper = m.isDropper || false;
        return product;
      })
    );

    const validProducts = products.filter(p => p && p.available !== false);

    if (validProducts.length === 0) {
      this.els.upsells.innerHTML = '';
      return;
    }

    let html = `<div class="cd-upsells__heading">${this.esc(heading)}</div>`;
    html += '<div class="cd-upsells__list">';
    validProducts.forEach(product => { html += this.renderUpsellCard(product); });
    html += '</div>';
    this.els.upsells.innerHTML = html;
  }

  renderUpsellCard(product) {
    const variant = product.variants[0];

    // .js endpoint: featured_image is a plain URL string, images is array of strings
    let img = '';
    if (product.featured_image) {
      img = typeof product.featured_image === 'string'
        ? product.featured_image
        : (product.featured_image.src || product.featured_image.url || '');
    } else if (product.images && product.images.length > 0) {
      const firstImg = product.images[0];
      img = typeof firstImg === 'string' ? firstImg : (firstImg.src || '');
    }

    // .js endpoint: prices are already in cents (integers)
    const price = parseInt(variant.price) || 0;
    const comparePrice = parseInt(variant.compare_at_price) || 0;
    const hasCompare = comparePrice > price;

    // Check for selling plans (Recharge subscription)
    const sellingPlanGroups = product.selling_plan_groups || [];
    const hasSubscription = sellingPlanGroups.length > 0;
    const cardId = `upsell-${variant.id}`;

    let subscribeHtml = '';
    if (hasSubscription) {
      // Collect all plans across groups, deduplicate by name, prefer highest discount
      const bestPlans = this.getBestSellingPlans(sellingPlanGroups);
      const topDiscount = bestPlans.reduce((max, p) => Math.max(max, p._discountPct || 0), 0);
      const discountText = topDiscount > 0 ? `& save up to ${topDiscount}%` : '';

      subscribeHtml = `
        <div class="cd-subscribe" data-card-id="${cardId}">
          <div class="cd-subscribe__toggle">
            <label class="cd-subscribe__option is-active">
              <input type="radio" name="purchase-${cardId}" value="onetime" checked>
              <span>One-time</span>
            </label>
            <label class="cd-subscribe__option">
              <input type="radio" name="purchase-${cardId}" value="subscribe">
              <span>Subscribe${discountText ? ` ${discountText}` : ''}</span>
            </label>
          </div>
          <div class="cd-subscribe__freq-wrap" style="display:none">
            <select class="cd-subscribe__freq" aria-label="Delivery frequency">
              ${bestPlans.map(p => `<option value="${p.id}">${this.esc(p.name)}${p._discountPct ? ` (save ${p._discountPct}%)` : ''}</option>`).join('')}
            </select>
          </div>
        </div>`;
    }

    // Build variant selector if multiple variants exist
    let variantSelectHtml = '';
    let defaultVariantId = variant.id;
    if (product.variants.length > 1) {
      variantSelectHtml = `
        <div class="cd-upsell__variant-wrap">
          <select class="cd-upsell__variant-select" data-upsell-variant-select>
            ${product.variants.filter(v => v.available).map(v => `<option value="${v.id}" data-price="${v.price}">${this.esc(v.title)}</option>`).join('')}
          </select>
        </div>`;
    }

    return `
      <div class="cd-upsell${product._isDropper ? ' cd-upsell--dropper' : ''}" data-upsell-card id="${cardId}">
        <div class="cd-upsell__top">
          <div class="cd-upsell__img">
            <img src="${img}" alt="${this.esc(product.title)}" loading="lazy" width="64" height="64">
          </div>
          <div class="cd-upsell__info">
            <div class="cd-upsell__title">${this.esc(product.title)}</div>
            <div class="cd-upsell__price">
              ${hasCompare ? `<span class="cd-upsell__price-compare">${this.money(comparePrice)}</span>` : ''}
              <span>${this.money(price)}</span>
            </div>
          </div>
        </div>
        ${variantSelectHtml}
        ${subscribeHtml}
        <button class="cd-upsell__add" data-action="add-upsell" data-variant-id="${defaultVariantId}">Add</button>
      </div>`;
  }

  /**
   * Collects selling plans from ALL groups, deduplicates by name,
   * prefers the plan with the highest discount when duplicates exist.
   */
  getBestSellingPlans(groups) {
    const seen = {};
    for (const group of groups) {
      for (const plan of (group.selling_plans || [])) {
        const key = (plan.name || '').toLowerCase().trim();
        const pct = this.getPlanDiscountPct(plan);
        if (!seen[key] || pct > (seen[key]._discountPct || 0)) {
          seen[key] = { ...plan, _discountPct: pct };
        }
      }
    }
    return Object.values(seen);
  }

  /**
   * Extracts percentage discount from a selling plan
   */
  getPlanDiscountPct(plan) {
    if (!plan || !plan.price_adjustments || plan.price_adjustments.length === 0) return 0;
    const adj = plan.price_adjustments[0];
    if (adj.value_type === 'percentage' && adj.value > 0) return adj.value;
    return 0;
  }

  /**
   * Handles subscribe/one-time toggle on upsell cards
   */
  handleSubscribeToggle(radio) {
    const card = radio.closest('[data-upsell-card]');
    if (!card) return;
    const freqWrap = card.querySelector('.cd-subscribe__freq-wrap');
    if (freqWrap) {
      freqWrap.style.display = radio.value === 'subscribe' ? '' : 'none';
    }
    // :has() fallback — toggle .is-active on all labels in this group
    const allLabels = card.querySelectorAll('.cd-subscribe__option');
    allLabels.forEach(label => {
      const inp = label.querySelector('input');
      label.classList.toggle('is-active', inp && inp.checked);
    });
  }

  handleFrequencyChange(select) {
    // Frequency stored in the select value, read on add-to-cart
  }

  async getProduct(handle) {
    if (this.productCache[handle]) return this.productCache[handle];
    try {
      // .js endpoint includes selling_plan_groups; .json does NOT
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      const data = await res.json();
      // .js returns the product directly (no wrapper)
      this.productCache[handle] = data;
      return data;
    } catch (e) {
      console.error('Failed to fetch product:', handle, e);
      return null;
    }
  }

  /* ── Footer ─────────────────────────────────── */

  renderFooter(cart) {
    const totalCount = cart.items.reduce((sum, i) => sum + i.quantity, 0);

    if (this.els.itemCount) {
      this.els.itemCount.textContent = `Subtotal (${totalCount} item${totalCount !== 1 ? 's' : ''})`;
    }
    if (this.els.subtotalPrice) {
      this.els.subtotalPrice.textContent = this.money(cart.total_price);
    }
    if (this.els.subtotalCompare) {
      const compareTotal = cart.original_total_price;
      if (compareTotal > cart.total_price) {
        this.els.subtotalCompare.textContent = this.money(compareTotal);
        this.els.subtotalCompare.style.display = '';
      } else {
        this.els.subtotalCompare.style.display = 'none';
      }
    }

    // Update checkout button text with price
    const checkoutBtn = this.drawer.querySelector('[data-checkout-btn]');
    if (checkoutBtn) {
      checkoutBtn.textContent = `Checkout • ${this.money(cart.total_price)}`;
    }
  }

  /* ── Utilities ──────────────────────────────── */

  money(cents) {
    const pounds = (cents / 100).toFixed(2);
    return `£${pounds}`;
  }

  esc(str) {
    const d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }
}

/* ── Init ──────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  const drawer = document.getElementById('metabolics-cart-drawer');
  if (drawer && drawer.dataset.cartConfig) {
    window.metabolicsCart = new MetabolicsCartDrawer(drawer);
  }
});

/* ── Intercept add-to-cart forms to open drawer ── */

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[action*="/cart/add"]');
  if (!form || form.dataset.noDrawer) return;

  e.preventDefault();
  const formData = new FormData(form);

  try {
    await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    });
    if (window.metabolicsCart) {
      window.metabolicsCart.open();
    }
  } catch (err) {
    form.submit();
  }
});
